import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/cms/ui/sheet';
import { StoryTemplateSummaryCard } from '@/components/cms/StoryTemplateSummaryCard';
import { PILL_BASE, PILL_DOT, SUBMISSION_PILL } from '@/components/cms/ui/pill-styles';
import type { TemplateSubmission, TemplateUsageSummary } from '@/types/templateSubmissionTypes';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateSceneOriginId: string | null;
  submission: TemplateSubmission | null;
  usageSummary?: TemplateUsageSummary | null;
}

// The template copy has no page of its own: it is a published snapshot, rebuilt on
// every resubmission, so there is nothing on it to navigate to or edit. Its listing
// is shown here, beside the story that owns it.
export function TemplateDetailsPanel({
  open,
  onOpenChange,
  templateSceneOriginId,
  submission,
  usageSummary,
}: Props) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-xl"
        aria-describedby={undefined}
      >
        <SheetHeader>
          <SheetTitle>Your template</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {submission && (
            <div className="flex flex-wrap items-center gap-3 rounded-lureo-radius border-lureo-border-width border-lureo-line bg-lureo-neutral-subtle/80 px-4 py-3">
              <span className={`${PILL_BASE} shrink-0 px-3 ${SUBMISSION_PILL[submission.status]}`}>
                <span className={PILL_DOT} />
                {STATUS_LABEL[submission.status]}
              </span>
              <p className="min-w-0 flex-1 text-lureo-14 text-lureo-ink leading-5">
                {statusText(submission.status, usageSummary)}
              </p>
            </div>
          )}

          {submission?.status === 'rejected' && submission.rejectionReason && (
            <div className="rounded-lureo-radius-lg border-lureo-border-width-medium border-lureo-coral-ink bg-lureo-coral/10 px-4 py-3 text-lureo-14 text-lureo-ink leading-5">
              <p className="font-medium text-lureo-brand-ink">Reviewer feedback</p>
              <p className="mt-1 whitespace-pre-wrap">{submission.rejectionReason}</p>
            </div>
          )}

          <StoryTemplateSummaryCard
            templateSceneOriginId={templateSceneOriginId}
            submissionStatus={submission?.status ?? null}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

const STATUS_LABEL = {
  draft: 'Draft',
  submitted: 'In review',
  accepted: 'Accepted',
  rejected: 'Not accepted',
} as const;

function statusText(
  status: keyof typeof STATUS_LABEL,
  usage: TemplateUsageSummary | null | undefined,
): string {
  switch (status) {
    case 'submitted':
      return "With our reviewers. We'll let you know within 7 days.";
    case 'accepted': {
      const uses = usage?.qualifyingUseCount ?? 0;
      if (uses < 1) {
        return "This template is live! You'll earn when someone publishes their own version.";
      }
      return `This template is live! ${uses} ${
        uses === 1 ? 'person has' : 'people have'
      } published a qualifying version you could earn from.`;
    }
    case 'rejected':
      return 'Not accepted. Address the feedback, then submit it again.';
    case 'draft':
      return 'A draft submission that has not been sent yet.';
  }
}
